import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';

import { StatusBadge } from '../../../../../components/atoms/StatusBadge';

interface StatusOption {
  code: string;
  name: string;
}

const StatusChip: React.FC<{ name: string; selected: boolean; onClick: () => void }> = ({
  name,
  selected,
  onClick,
}) => {
  const theme = useTheme();
  return (
    <ButtonBase
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      focusRipple
      sx={{
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        border: '1px solid',
        borderColor: selected ? theme.palette.v4.accent : theme.palette.v4.border,
        bgcolor: selected ? `${theme.palette.v4.accent}10` : 'transparent',
        display: 'flex',
        alignItems: 'center',
        '&:hover': { borderColor: theme.palette.v4.accent },
        '&.Mui-focusVisible': {
          borderColor: theme.palette.v4.accent,
          boxShadow: `0 0 0 2px ${theme.palette.v4.accent}40`,
        },
      }}
    >
      <StatusBadge status={name} />
    </ButtonBase>
  );
};

export interface StatusChangeDialogProps {
  open: boolean;
  onClose: () => void;
  currentStatus: string;
  possibleStatuses: StatusOption[];
  preferredStatuses: StatusOption[];
  loading: boolean;
  onSubmit: (statusCode: string, notes: string) => void;
  submitting: boolean;
}

export const StatusChangeDialog: React.FC<StatusChangeDialogProps> = ({
  open,
  onClose,
  currentStatus,
  possibleStatuses,
  preferredStatuses,
  loading,
  onSubmit,
  submitting,
}) => {
  const theme = useTheme();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedStatus('');
      setNotes('');
    }
  }, [open]);

  // The BFF's `preferedStatuses` is a *recommendation* set that isn't
  // guaranteed to be a subset of `possibleNextStatuses`. Any preferred code
  // that isn't also "possible" would be rejected by the BFF with
  // `cannot set selected status`, so we intersect before rendering.
  const possibleCodes = new Set(possibleStatuses.map((s) => s.code));
  const preferredCodes = new Set(
    preferredStatuses.filter((s) => possibleCodes.has(s.code)).map((s) => s.code)
  );
  const byName = (a: StatusOption, b: StatusOption) => a.name.localeCompare(b.name);
  const preferredSection = possibleStatuses.filter((s) => preferredCodes.has(s.code)).sort(byName);
  const otherSection = possibleStatuses.filter((s) => !preferredCodes.has(s.code)).sort(byName);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>Change Status</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary', mb: 0.5 }}>
            Current Status
          </Typography>
          <StatusBadge status={currentStatus} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', mb: 0.75 }}>
              Select New Status
            </Typography>

            {preferredSection.length === 0 && otherSection.length === 0 && (
              <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 2 }}>
                No available status transitions
              </Typography>
            )}

            {preferredSection.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: theme.palette.v4.accent,
                    mb: 0.5,
                  }}
                >
                  Preferred
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {preferredSection.map((status) => (
                    <StatusChip
                      key={status.code}
                      name={status.name}
                      selected={selectedStatus === status.code}
                      onClick={() => setSelectedStatus(status.code)}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {otherSection.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: 'text.secondary',
                    mb: 0.5,
                  }}
                >
                  Other
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {otherSection.map((status) => (
                    <StatusChip
                      key={status.code}
                      name={status.name}
                      selected={selectedStatus === status.code}
                      onClick={() => setSelectedStatus(status.code)}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <TextField
              label="Notes (optional)"
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px' } }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(selectedStatus, notes)}
          disabled={!selectedStatus || submitting}
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.v4.accent,
            '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 },
          }}
        >
          {submitting ? (
            <CircularProgress size={16} sx={{ color: 'common.white' }} />
          ) : (
            'Change Status'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
